import { validations } from "./Validations";
import { Forms, Tooltip } from "@types/FormTypes";
import { __ } from "@wordpress/i18n";
import { helpers } from "../../../helpers/forms/helpers";

/**
 * Labels used for the form fields in the property valuation simulator.
 */
const LABELS = {
    simulationData: __("Dados para a simulação", "df-simulators"),
    address: __("Morada ou código postal", "df-simulators"),
    assetType: __("Tipo de imóvel", "df-simulators"),
    condition: __("Condição", "df-simulators"),
    grossArea: __("Área bruta m²", "df-simulators"),
    usefullArea: __("Área útil m²", "df-simulators"),
    terrainArea: __("Área de terreno m²", "df-simulators"),
    constructionYear: __("Ano de construção", "df-simulators"),
    numberOfRooms: __("Tipologia", "df-simulators"),
    propertyCharacteristics: __("Características do imóvel", "df-simulators"),
    elevator: __("Elevador", "df-simulators"),
    closedGarage: __("Garagem", "df-simulators"),
    doubleWindows: __("Vidros duplos", "df-simulators"),
    propertyIntention: __("O que pretende fazer com este imóvel?", "df-simulators"),
    timingPlan: __("Quando está a pensar fazê-lo?", "df-simulators"),
};

/**
 * Tooltips for form fields providing additional context to users.
 */
const TOOLTIPS: Record<string, Tooltip> = {
    constructionYear: {
        title: __("Ano de construção", "df-simulators"),
        content: __("Selecione o ano de construção do imóvel.", "df-simulators"),
        placement: "right",
    },
};


/**
 * API configuration constants for the simulator.
 */
const API_BASE_URL = `${window.location.origin}/wp-json/simulators/v1/proptech`;
const PROCESS_TYPE = 4;
const OPERATION_TYPE = 2;


/**
 * Simulator custom URL - Check if exists some custom URL for this block
 * [==START==]
 */
let MIDDLEWARE_API_URL = API_BASE_URL;
const rootElement = document.querySelector('[data-block="tool-block"]') as HTMLElement | null;
if ( rootElement !== null ) {
  const NEW_URL = rootElement.getAttribute('data-tool-managment');
  if ( NEW_URL !== null && NEW_URL !== '' ) {
    MIDDLEWARE_API_URL = NEW_URL;
  }
}
/* [==END==] */


/**
 * Form configuration for the property valuation simulator.
 */
export const forms: Forms = {
    property_valuation_proptech: {
        validation: validations.property_valuation_proptech,
        api: {
            url: MIDDLEWARE_API_URL,
            processType: PROCESS_TYPE,
            operationType: OPERATION_TYPE,
        },
        fields: [
            {
                type: 'title',
                label: LABELS.simulationData,
            },
            {
                type: 'map',
                label: LABELS.address,
                name: 'address',
                placeholder: __("Introduza a Morada", "df-simulators"),
                usesAutocomplete: true, // Check for autocomplete, in a fields.type agnostic way.
                latField: 'latitude',
                lonField: 'longitude',
            },
            {
                type: 'select',
                options: [
                    { value: 'apartment', label: __('Apartamento', 'df-simulators') },
                    { value: 'house', label: __('Moradia', 'df-simulators') },
                ],
                label: LABELS.assetType,
                name: 'assetType',
            },
            {
                type: 'select',
                options: [
                    { value: 'new', label: __('Novo', 'df-simulators') },
                    { value: 'used', label: __('Usado', 'df-simulators') },
                    { value: 'renovated', label: __('Renovado', 'df-simulators') },
                    { value: 'inConstruction', label: __('Em construção', 'df-simulators') },
                    { value: 'forRecovery', label: __('Para recuperação', 'df-simulators') },
                    { value: 'ruin', label: __('Em ruína', 'df-simulators') },
                ],
                label: LABELS.condition,
                name: 'condition',
            },
            {
                type: 'number',
                label: LABELS.grossArea,
                name: 'grossArea',
                placeholder: "100",
            },
            {
                type: 'number',
                label: LABELS.usefullArea,
                name: 'usefullArea',
                placeholder: "100",
            },
            {
                type: 'number',
                label: LABELS.terrainArea,
                name: 'terrainArea',
                placeholder: "100",
                condition: {
                    field: "assetType",
                    value: "house",  // Show only if assetType is "house"
                },
            },
            {
                type: 'select',
                options: helpers.generateYears(),
                label: LABELS.constructionYear,
                name: 'constructionYear',
            },
            {
                type: 'select',
                options: [
                    { value: 0, label: 'T0' },
                    { value: 1, label: 'T1' },
                    { value: 2, label: 'T2' },
                    { value: 3, label: 'T3' },
                    { value: 4, label: 'T4' },
                    { value: 5, label: 'T5' },
                    { value: 6, label: 'T6' },
                    { value: 7, label: 'T7' },
                    { value: 8, label: 'T8' },
                    { value: 9, label: 'T9' },
                    { value: 10, label: 'T10' },
                ],
                label: LABELS.numberOfRooms,
                name: 'numberOfRooms',
            },
            {
                type: 'heading',
                label: LABELS.propertyCharacteristics,
            },
            {
                type: 'checkbox',
                label: LABELS.elevator,
                name: 'elevator',
                className: '-mb-4',
            },
            {
                type: 'checkbox',
                label: LABELS.closedGarage,
                name: 'closedGarage',
                className: '-mb-4',
            },
            {
                type: 'checkbox',
                label: LABELS.doubleWindows,
                name: 'doubleWindows',
            },
            {
                type: 'heading',
                label: LABELS.propertyIntention,
            },
            {
                type: 'select',
                options: [
                    { value: 'Estou a pensar vender este imóvel', label: __('Estou a pensar vender este imóvel', 'df-simulators') },
                    { value: 'Estou a pensar comprar este imóvel', label: __('Estou a pensar comprar este imóvel', 'df-simulators') },
                    { value: 'Quero apenas avaliar o meu património', label: __('Quero apenas avaliar o meu património', 'df-simulators') },
                ],
                name: 'property_intention',
                full: true,
            },
            {
                type: 'heading',
                label: LABELS.timingPlan,
            },
            {
                type: 'select',
                options: [
                    { value: 'Nos próximos 3 meses', label: __('Nos próximos 3 meses', 'df-simulators') },
                    { value: 'Nos próximos 6 meses', label: __('Nos próximos 6 meses', 'df-simulators') },
                    { value: 'Nos próximos 12 meses', label: __('Nos próximos 12 meses', 'df-simulators') },
                    { value: 'Não Aplicável', label: __('Não Aplicável', 'df-simulators') },
                ],
                name: 'timing_plan',
                full: true,
            },
        ],
    },
};
